import { type FormEvent, useEffect, useState } from 'react';
import {
  health,
  listMailboxes,
  upsertMailbox,
  type MailboxItem,
} from '../api/client';
import { useAuth } from '../lib/auth';

export default function SettingsPage() {
  const { token, apiBase, setToken, setBase, logout } = useAuth();
  const [tokenInput, setTokenInput] = useState(token);
  const [baseInput, setBaseInput] = useState(apiBase);
  const [mailboxes, setMailboxes] = useState<MailboxItem[]>([]);
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [healthInfo, setHealthInfo] = useState<string>('-');

  async function refreshMailboxes() {
    try {
      const data = await listMailboxes();
      setMailboxes(data.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void refreshMailboxes();
    health()
      .then((h) => setHealthInfo(`${h.name} · ${h.time}`))
      .catch(() => setHealthInfo('不可用'));
  }, []);

  function saveAuth(e: FormEvent) {
    e.preventDefault();
    setBase(baseInput.trim());
    setToken(tokenInput.trim());
    setMessage('已保存到本机 localStorage');
    setError(null);
  }

  async function addMailbox(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    try {
      await upsertMailbox({ address: address.trim(), label: label.trim(), enabled: true });
      setAddress('');
      setLabel('');
      setMessage('邮箱已保存');
      await refreshMailboxes();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="settings-grid">
      {error && <div className="error-banner">{error}</div>}
      {message && (
        <div
          className="error-banner"
          style={{
            background: 'rgba(61, 214, 198, 0.12)',
            borderColor: 'rgba(61, 214, 198, 0.4)',
            color: '#b8fff6',
          }}
        >
          {message}
        </div>
      )}

      <section className="panel card">
        <h2>连接设置</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Token 与 API Base 仅保存在浏览器 localStorage，不会上传到第三方。
        </p>
        <form onSubmit={saveAuth}>
          <div className="form-row">
            <label htmlFor="base">API Base URL</label>
            <input
              id="base"
              className="input"
              value={baseInput}
              onChange={(e) => setBaseInput(e.target.value)}
              placeholder="https://cf-mail-inbox.xxx.workers.dev"
            />
          </div>
          <div className="form-row">
            <label htmlFor="token">Bearer Token</label>
            <input
              id="token"
              className="input"
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary">
              保存
            </button>
            <button type="button" className="btn" onClick={logout}>
              清除 Token
            </button>
          </div>
        </form>
        <p className="muted" style={{ marginBottom: 0, marginTop: 12 }}>
          Health: {healthInfo}
        </p>
      </section>

      <section className="panel card">
        <h2>邮箱管理</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          收信时会自动登记 mailbox；也可在此手动添加标签。实际收信地址需在 Cloudflare Email
          Routing 中配置。
        </p>
        <form onSubmit={addMailbox} className="toolbar" style={{ marginBottom: 12 }}>
          <input
            className="input"
            placeholder="address@example.com"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            required
          />
          <input
            className="input"
            placeholder="标签（可选）"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <button type="submit" className="btn btn-primary">
            添加/更新
          </button>
        </form>
        <table className="table">
          <thead>
            <tr>
              <th>地址</th>
              <th>标签</th>
              <th>状态</th>
            </tr>
          </thead>
          <tbody>
            {mailboxes.map((m) => (
              <tr key={m.address}>
                <td>{m.address}</td>
                <td>{m.label || '-'}</td>
                <td>{m.enabled ? '启用' : '停用'}</td>
              </tr>
            ))}
            {!mailboxes.length && (
              <tr>
                <td colSpan={3} className="muted">
                  暂无邮箱记录
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="panel card">
        <h2>关于</h2>
        <ul className="muted" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
          <li>架构：Email Routing → Worker → R2(.eml) + D1(索引) → API → Pages</li>
          <li>收信时不完整解析 MIME；打开邮件时从 R2 实时解析</li>
          <li>HTML 预览经 DOMPurify 净化，iframe sandbox 隔离</li>
        </ul>
      </section>
    </div>
  );
}
